import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import { Banner, EmptyState, PageLoading, Select } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';

import type {
  Area, CompanySettings, ScheduleCalendar, ScheduleSeries, ScheduleSummary, TeamMember,
} from '../../types';

/**
 * Route scheduling — the beat as a calendar.
 *
 * A schedule assigns a whole route to a rep on chosen weekdays between two
 * dates; the calendar then shows, day by day, which routes each rep works.
 * A manager can change any single day without disturbing the recurring
 * series behind it. The rep's app beat for a date is exactly the shops in
 * the routes shown here for that date.
 */

const WEEKDAYS = [
  { weekday: 1, short: 'Mon' },
  { weekday: 2, short: 'Tue' },
  { weekday: 3, short: 'Wed' },
  { weekday: 4, short: 'Thu' },
  { weekday: 5, short: 'Fri' },
  { weekday: 6, short: 'Sat' },
  { weekday: 7, short: 'Sun' },
];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
function todayYmd() { const t = new Date(); return ymd(t.getFullYear(), t.getMonth() + 1, t.getDate()); }
function isoWeekday(y: number, m: number, d: number) {
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return dow === 0 ? 7 : dow;
}
function daysInMonth(y: number, m: number) { return new Date(y, m, 0).getDate(); }

function monthGrid(year: number, month: number): (string | null)[][] {
  const first = isoWeekday(year, month, 1);
  const total = daysInMonth(year, month);
  const cells: (string | null)[] = [];
  for (let i = 1; i < first; i += 1) cells.push(null);
  for (let d = 1; d <= total; d += 1) cells.push(ymd(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

export default function JourneyPlansPage() {
  const toast = useToast();

  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [reps, setReps] = useState<TeamMember[]>([]);
  const [routes, setRoutes] = useState<Area[]>([]);
  const [repId, setRepId] = useState<string | null>(null);

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1..12

  const [summary, setSummary] = useState<ScheduleSummary | null>(null);
  const [calendar, setCalendar] = useState<ScheduleCalendar | null>(null);
  const [series, setSeries] = useState<ScheduleSeries[]>([]);

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingDate, setEditingDate] = useState<string | null>(null);

  const from = ymd(year, month, 1);
  const to = ymd(year, month, daysInMonth(year, month));

  // First load: settings + reps (routes load per-rep below).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, team] = await Promise.all([
          api.get<CompanySettings>('/settings'),
          api.get<TeamMember[]>('/users'),
        ]);
        if (cancelled) return;
        setSettings(s);
        const salesReps = team.filter((m) => m.role === 'sales_rep');
        setReps(salesReps);
        setRepId(salesReps[0]?.id ?? null);
      } catch (err) {
        if (!cancelled) toast.error(err instanceof ApiClientError ? err.message : 'Could not load scheduling.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [toast]);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await api.get<ScheduleSummary>('/visits/schedule/summary', { from, to }));
    } catch { /* non-fatal */ }
  }, [from, to]);

  const loadRep = useCallback(async (userId: string) => {
    try {
      const [cal, list] = await Promise.all([
        api.get<ScheduleCalendar>('/visits/schedule/calendar', { userId, from, to }),
        api.get<ScheduleSeries[]>('/visits/schedule/list', { userId }),
      ]);
      setCalendar(cal);
      setSeries(list);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load the calendar.');
    }
  }, [from, to, toast]);

  useEffect(() => { void loadSummary(); }, [loadSummary]);
  useEffect(() => { if (repId) void loadRep(repId); }, [repId, loadRep]);

  // Reload routes filtered by the selected rep's assignments.
  useEffect(() => {
    if (!repId) { setRoutes([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const rts = await api.get<Area[]>('/customers/routes', { status: 'active', userId: repId });
        if (!cancelled) setRoutes(rts);
      } catch { /* non-fatal — routes list stays stale */ }
    })();
    return () => { cancelled = true; };
  }, [repId]);

  const routesByDate = useMemo(() => {
    const map = new Map<string, ScheduleCalendar['days'][number]['routes']>();
    for (const d of calendar?.days ?? []) map.set(d.date, d.routes);
    return map;
  }, [calendar]);

  function stepMonth(delta: number) {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  }

  async function refresh() {
    if (repId) await loadRep(repId);
    await loadSummary();
  }

  if (loading) {
    return (
      <>
        <PageHeader title="Route schedule" />
        <div className="page"><PageLoading /></div>
      </>
    );
  }

  if (settings?.businessModel === 'service') {
    return (
      <>
        <PageHeader title="Route schedule" />
        <div className="page">
          <div className="card">
            <EmptyState
              icon="map"
              title="Schedules do not apply to service work"
              body="A route schedule plans a beat of shops. Your team goes where the jobs
                    are, so there is no route to schedule — which is why this is off in a
                    service business."
            />
          </div>
        </div>
      </>
    );
  }

  const grid = monthGrid(year, month);
  const today = todayYmd();

  return (
    <>
      <PageHeader
        title="Route schedule"
        subtitle="Which routes each rep works, day by day."
      />

      <div className="page col gap-16">
        {!settings?.journeyPlanEnabled ? (
          <Banner kind="warning">
            <strong>Route scheduling is switched off.</strong>
            <div style={{ marginTop: 4 }}>
              Your reps currently see every shop in the areas they cover. You can build
              schedules here, but they only drive the app beat once you turn scheduling on
              in <a href="/settings">Settings</a>.
            </div>
          </Banner>
        ) : null}

        {reps.length === 0 ? (
          <div className="card">
            <EmptyState icon="id" title="No salesmen yet"
              body="Add your team first, then schedule their routes." />
          </div>
        ) : (
          <>
            {/* Overview: scheduled days per rep this month */}
            <div className="card">
              <div className="card-head">
                <h3>Scheduled days · {MONTHS[month - 1]} {year}</h3>
                <span className="small faint">Distinct days each rep has a route</span>
              </div>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr><th>Rep</th><th className="num">Days</th><th className="num">Route-days</th></tr>
                  </thead>
                  <tbody>
                    {(summary?.reps ?? []).map((rep) => (
                      <tr
                        key={rep.userId}
                        className={`clickable${rep.userId === repId ? ' selected' : ''}`}
                        onClick={() => setRepId(rep.userId)}
                      >
                        <td className="nowrap">{rep.name}</td>
                        <td className="num">{rep.days || <span className="faint">·</span>}</td>
                        <td className="num">{rep.assignments || <span className="faint">·</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Calendar for the selected rep */}
            <div className="card">
              <div className="card-head">
                <div className="row gap-8">
                  <button type="button" className="icon-btn" aria-label="Previous month"
                    onClick={() => stepMonth(-1)}><Icon name="chevron-left" size={18} /></button>
                  <h3 style={{ minWidth: 150, textAlign: 'center' }}>{MONTHS[month - 1]} {year}</h3>
                  <button type="button" className="icon-btn" aria-label="Next month"
                    onClick={() => stepMonth(1)}><Icon name="chevron-right" size={18} /></button>
                </div>
                <div className="row gap-8">
                  <Select value={repId ?? ''} onChange={(e) => setRepId(e.target.value)} aria-label="Rep">
                    {reps.map((r) => <option key={r.id} value={r.id}>{r.fullName}</option>)}
                  </Select>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
                    <Icon name="plus" size={15} /> New schedule
                  </button>
                </div>
              </div>

              <div className="card-pad">
                <div className="cal-grid cal-head">
                  {WEEKDAYS.map((d) => <div key={d.weekday} className="cal-dow">{d.short}</div>)}
                </div>
                {grid.map((week, wi) => (
                  <div className="cal-grid" key={wi}>
                    {week.map((date, di) => {
                      if (!date) return <div className="cal-cell empty" key={di} />;
                      const dayRoutes = routesByDate.get(date) ?? [];
                      const dnum = Number(date.slice(8, 10));
                      return (
                        <button
                          type="button"
                          key={di}
                          className={`cal-cell${date === today ? ' today' : ''}`}
                          onClick={() => setEditingDate(date)}
                          title="Click to change this day"
                        >
                          <span className="cal-date">{dnum}</span>
                          <span className="cal-routes">
                            {dayRoutes.map((r) => (
                              <span key={r.routeId} className={`cal-chip${r.manual ? ' manual' : ''}`}>
                                {r.route}
                              </span>
                            ))}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
                <div className="row gap-16 small faint" style={{ marginTop: 10 }}>
                  <span><span className="cal-chip" style={{ display: 'inline-block' }}>Route</span> from a schedule</span>
                  <span><span className="cal-chip manual" style={{ display: 'inline-block' }}>Route</span> set for this day only</span>
                </div>
              </div>
            </div>

            {/* Active recurring schedules */}
            <div className="card">
              <div className="card-head"><h3>Recurring schedules</h3></div>
              <div className="card-pad">
                {series.length === 0 ? (
                  <EmptyState
                    title="No recurring schedules"
                    body="Use “New schedule” to assign a route on chosen weekdays across a date range.
                          You can still set any single day directly on the calendar."
                  />
                ) : (
                  <div className="col gap-8">
                    {series.map((s) => (
                      <div key={s.id} className="row between" style={{
                        padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
                      }}>
                        <div className="col gap-4">
                          <strong>{s.route}</strong>
                          <span className="small faint">
                            {s.weekdays.map((w) => WEEKDAYS.find((d) => d.weekday === w)?.short ?? '').join(', ')}
                            {' · '}{s.startDate} → {s.endDate}
                            {s.upcoming ? ` · ${s.upcoming} upcoming` : ''}
                          </span>
                        </div>
                        <button
                          type="button" className="icon-btn danger" aria-label={`Delete ${s.route} schedule`}
                          onClick={() => void deleteSeries(s.id)}
                        ><Icon name="trash" size={16} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {creating && repId ? (
        <NewScheduleModal
          routes={routes}
          onClose={() => setCreating(false)}
          onCreate={async (payload) => {
            try {
              await api.post('/visits/schedule', { userId: repId, ...payload });
              setCreating(false);
              await refresh();
              toast.success('Schedule created.');
            } catch (err) {
              toast.error(err instanceof ApiClientError ? err.message : 'Could not create the schedule.');
            }
          }}
        />
      ) : null}

      {editingDate && repId ? (
        <DayEditor
          date={editingDate}
          routes={routes}
          current={routesByDate.get(editingDate) ?? []}
          onClose={() => setEditingDate(null)}
          onSave={async (routeIds) => {
            try {
              await api.put('/visits/schedule/day', { userId: repId, date: editingDate, routeIds });
              setEditingDate(null);
              await refresh();
            } catch (err) {
              toast.error(err instanceof ApiClientError ? err.message : 'Could not save that day.');
            }
          }}
        />
      ) : null}
    </>
  );

  async function deleteSeries(id: string) {
    try {
      await api.del(`/visits/schedule/${id}`);
      await refresh();
      toast.success('Schedule removed. Days already worked are kept.');
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not remove that schedule.');
    }
  }
}

// =====================================================================

function NewScheduleModal({
  routes, onClose, onCreate,
}: {
  routes: Area[];
  onClose: () => void;
  onCreate: (p: { routeIds: string[]; weekdays: number[]; startDate: string; endDate: string }) => void;
}) {
  const [routeIds, setRouteIds] = useState<string[]>([]);
  const [weekdays, setWeekdays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [startDate, setStartDate] = useState(todayYmd());
  const [endDate, setEndDate] = useState('');

  const valid = routeIds.length > 0 && weekdays.length > 0 && startDate && endDate && startDate <= endDate;

  return (
    <Modal
      title="New schedule"
      subtitle="Assign route(s) on chosen weekdays across a date range."
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid}
            onClick={() => onCreate({ routeIds, weekdays, startDate, endDate })}>
            Create schedule
          </button>
        </>
      )}
    >
      <div className="col gap-16">
        <div className="col gap-8">
          <label className="field-label">Routes</label>
          <div className="pick-list">
            {routes.map((r) => (
              <label key={r.id} className="pick-row">
                <input type="checkbox" checked={routeIds.includes(r.id)}
                  onChange={(e) => setRouteIds((c) => (e.target.checked ? [...c, r.id] : c.filter((x) => x !== r.id)))} />
                <span className="grow">
                  {r.name}
                  {typeof r.customerCount === 'number'
                    ? <span className="small faint"> · {r.customerCount} shop{r.customerCount === 1 ? '' : 's'}</span>
                    : null}
                </span>
              </label>
            ))}
            {routes.length === 0 ? <div className="small faint" style={{ padding: 12 }}>No active routes yet.</div> : null}
          </div>
        </div>

        <div className="col gap-8">
          <label className="field-label">Repeat on</label>
          <div className="row gap-8 wrap">
            {WEEKDAYS.map((d) => (
              <button key={d.weekday} type="button"
                className={`tab${weekdays.includes(d.weekday) ? ' active' : ''}`}
                onClick={() => setWeekdays((c) => (c.includes(d.weekday) ? c.filter((x) => x !== d.weekday) : [...c, d.weekday]))}>
                {d.short}
              </button>
            ))}
          </div>
        </div>

        <div className="row gap-16 wrap">
          <div className="col gap-4">
            <label className="field-label">Start date</label>
            <input type="date" className="input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="col gap-4">
            <label className="field-label">End date</label>
            <input type="date" className="input" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>
      </div>
    </Modal>
  );
}

function DayEditor({
  date, routes, current, onClose, onSave,
}: {
  date: string;
  routes: Area[];
  current: { routeId: string; route: string; manual: boolean }[];
  onClose: () => void;
  onSave: (routeIds: string[]) => void;
}) {
  const [chosen, setChosen] = useState<string[]>(current.map((r) => r.routeId));

  return (
    <Modal
      title={`Routes for ${date}`}
      subtitle="Sets this one day only — the recurring schedule is not changed."
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => onSave(chosen)}>
            Save this day
          </button>
        </>
      )}
    >
      <div className="pick-list">
        {routes.map((r) => (
          <label key={r.id} className="pick-row">
            <input type="checkbox" checked={chosen.includes(r.id)}
              onChange={(e) => setChosen((c) => (e.target.checked ? [...c, r.id] : c.filter((x) => x !== r.id)))} />
            <span className="grow">
              {r.name}
              {typeof r.customerCount === 'number'
                ? <span className="small faint"> · {r.customerCount} shop{r.customerCount === 1 ? '' : 's'}</span>
                : null}
            </span>
          </label>
        ))}
        {routes.length === 0 ? <div className="small faint" style={{ padding: 12 }}>No active routes yet.</div> : null}
      </div>
    </Modal>
  );
}
